/**
 * Member Area Layout Component
 * 
 * Provides consistent header and tab navigation across all member area pages.
 * This ensures tabs remain visible and clickable when navigating between sections.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import AICoPilot from '@/components/AICoPilot';
import { getAccess, isAllowed, isHidden as isPermHidden, isUpgradeRequired, getUpgradeTarget, FEATURES } from '@/permissions/permissionsMatrix';
import MessageDrawer from '@/components/MessageDrawer';
import TabNavigation from '@/components/TabNavigation';
import {
  Bell,
  HelpCircle,
  Settings,
  LogOut,
  Building2,
  Users,
  Crown,
  Activity,
  Download,
  Globe,
  Zap,
  Lightbulb,
  ChevronDown,
  Check,
  Menu,
  Lock,
  X,
  Mail,
  Plus,
  Shield
} from 'lucide-react';

export default function MemberLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [user, setUser] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [platformLogo, setPlatformLogo] = useState('/placeholder-logo.svg');

  // UI State
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [availableFYs, setAvailableFYs] = useState([]);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [tempDateRange, setTempDateRange] = useState({ start: null, end: null });
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeTarget, setUpgradeTarget] = useState(null); // 'pro' | 'standard_or_pro' | null

  // Add Organisation state
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [newOrgData, setNewOrgData] = useState({ name: '', abn: '', state: '', sector: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Australian states for org creation
  const australianStates = [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NT', name: 'Northern Territory' }
  ];

  // Sector options for org creation
  const sectorOptions = [
    'Agriculture, Forestry and Fishing',
    'Mining',
    'Manufacturing',
    'Electricity, Gas, Water and Waste Services',
    'Construction',
    'Wholesale Trade',
    'Retail Trade',
    'Accommodation and Food Services',
    'Transport, Postal and Warehousing',
    'Information Media and Telecommunications',
    'Financial and Insurance Services',
    'Rental, Hiring and Real Estate Services',
    'Professional, Scientific and Technical Services',
    'Administrative and Support Services',
    'Public Administration and Safety',
    'Education and Training',
    'Health Care and Social Assistance',
    'Arts and Recreation Services',
    'Other Services'
  ];

  // Message Centre state
  const [showMessageDrawer, setShowMessageDrawer] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // Selected financial year or date range
  // Can be: a year number (e.g., 2026), 'all', or 'custom'
  const [selectedYear, setSelectedYear] = useState(() => {
    const saved = localStorage.getItem('selectedFY');
    if (saved === 'all' || saved === 'custom') return saved;
    if (saved) return parseInt(saved);
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  });

  // Centralised permission check helper — uses the permissions matrix
  const effectiveTier = (() => {
    if (currentOrg?.subscription_override) {
      const overrideExpiry = currentOrg.subscription_override_expiry;
      if (!overrideExpiry || new Date(overrideExpiry) > new Date()) {
        return currentOrg.subscription_override.replace('manual_', '');
      }
    }
    return subscriptionStatus?.tier || currentOrg?.subscription_tier || 'demo';
  })();

  const effectiveRole = userRole?.role || 'viewer';
  const isSuperAdminOrAdmin = isAdmin || userRole?.is_superadmin;

  // Permission check that combines tier + role via the matrix
  const canAccess = (feature) => {
    // Admin/Superadmin bypass ONLY for the Admin Panel itself
    if (isSuperAdminOrAdmin && feature === FEATURES.SETTINGS_ADMIN_PANEL) {
      return true;
    }
    return isAllowed(getAccess(feature, effectiveTier, effectiveRole));
  };

  const getAccessResult = (feature) => {
    // Admin/Superadmin bypass ONLY for the Admin Panel itself
    if (isSuperAdminOrAdmin && feature === FEATURES.SETTINGS_ADMIN_PANEL) {
      return { allowed: true, reason: 'allowed', upgradeTarget: null, upgradeRequired: false, isHidden: false };
    }

    const access = getAccess(feature, effectiveTier, effectiveRole);
    const upgradeRequired = isUpgradeRequired(access);
    const allowed = isAllowed(access);

    return {
      allowed,
      reason: access,
      upgradeTarget: getUpgradeTarget(access),
      upgradeRequired,
      isHidden: isPermHidden(access)
    };
  };

  // Legacy canPerform for backward compat (used by Outlet context consumers)
  const canPerform = (action) => {
    if (!userRole) return false;
    if (userRole.is_superadmin) return true;
    const permissions = {
      owner: ['all'],
      full_access: ['add_entries', 'edit_entries', 'delete_entries', 'view_reports', 'export_data', 'manage_team', 'manage_billing', 'access_strategy', 'access_opportunities'],
      editor: ['add_entries', 'edit_entries', 'view_reports', 'access_opportunities'],
      viewer: ['view_reports']
    };
    const rolePerms = permissions[userRole.role] || [];
    return rolePerms.includes('all') || rolePerms.includes(action);
  };

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) {
          navigate('/login');
          return;
        }
        setUser(authUser);

        // Fetch platform logo
        try {
          const { data: systemSettings, error: systemError } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'platform_logo')
            .maybeSingle();

          if (systemSettings?.value && !systemError) {
            const parsed = typeof systemSettings.value === 'string' ? JSON.parse(systemSettings.value) : systemSettings.value;
            if (parsed?.logo_url) {
              setPlatformLogo(parsed.logo_url);
            }
          }
        } catch (e) {
          // Silently fail to fallback
        }

        // Fetch user record - check for both email AND supabase_user_id (Google OAuth fix)
        let userData = null;

        // Try by supabase_user_id first
        const { data: userById, error: userByIdError } = await supabase
          .from('users')
          .select('*')
          .eq('supabase_user_id', authUser.id)
          .single();

        if (userById) {
          userData = userById;
        } else {
          // Fallback: try by email (for Google OAuth users)
          const { data: userByEmail, error: userByEmailError } = await supabase
            .from('users')
            .select('*')
            .eq('email', authUser.email)
            .single();
          userData = userByEmail;
        }

        // If user doesn't exist in users table, create them (new signup)
        if (!userData) {
          const { v4: uuidv4 } = await import('uuid');
          const newUserId = uuidv4();

          const { data: newUser, error: createUserError } = await supabase
            .from('users')
            .insert({
              id: newUserId,
              email: authUser.email,
              name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
              supabase_user_id: authUser.id,
              role: 'user'
            })
            .select()
            .single();

          if (createUserError) {
            console.error('Error creating user record:', createUserError);
          } else {
            userData = newUser;
          }
        }

        if (userData) {
          setUserData(userData);
          // Check if superadmin (is_superadmin field in users table)
          const isSuperAdmin = userData.is_superadmin === true;
          setIsAdmin(isSuperAdmin);

          // Get organisations - check both owned and member orgs
          const { data: ownedOrgs } = await supabase
            .from('organisations')
            .select('*')
            .eq('owner_id', userData.id);

          // Fetch member orgs - handle RLS errors gracefully
          let memberOrgs = null;
          try {
            const { data, error } = await supabase
              .from('organisation_members')
              .select('organisation_id, role, organisations(*)')
              .eq('user_id', userData.id)
              .eq('invitation_status', 'accepted');
            if (!error) {
              memberOrgs = data;
            }
          } catch (e) {
            // organisation_members RLS may restrict access - continue with owned orgs only
          }

          // Combine all organisations
          const allOrgs = [
            ...(ownedOrgs || []).map(org => ({ ...org, user_role: 'owner' })),
            ...(memberOrgs?.map(om => ({ ...om.organisations, user_role: om.role })).filter(Boolean) || [])
          ];

          // Remove duplicates by id
          const uniqueOrgs = allOrgs.filter((org, index, self) =>
            index === self.findIndex(o => o.id === org.id)
          );

          if (uniqueOrgs.length > 0) {
            setOrganisations(uniqueOrgs);

            // Determine current org (priority: clean session default > local storage > fallback default > first org)
            let targetOrgData = undefined;
            const savedOrgId = localStorage.getItem('currentOrgId');
            const defaultOrgId = userData?.raw_user_meta_data?.default_organisation_id || authUser.user_metadata?.default_organisation_id;

            // On fresh session (e.g. new login or tab), prioritize default org if it exists
            if (!sessionStorage.getItem('session_started') && defaultOrgId) {
              targetOrgData = uniqueOrgs.find(o => o.id === defaultOrgId);
            }

            // Fallback to local storage (active switch during session)
            if (!targetOrgData && savedOrgId) {
              targetOrgData = uniqueOrgs.find(o => o.id === savedOrgId);
            }

            // Final fallback to default or first org
            if (!targetOrgData) {
              targetOrgData = uniqueOrgs.find(o => o.id === defaultOrgId) || uniqueOrgs[0];
            }

            setCurrentOrg(targetOrgData);
            if (targetOrgData) {
              localStorage.setItem('currentOrgId', targetOrgData.id);
              sessionStorage.setItem('session_started', 'true');
            }

            // Set user role for current org
            const currentOrgMember = memberOrgs?.find(om => om.organisation_id === targetOrgData?.id);
            const isOwner = ownedOrgs?.some(o => o.id === targetOrgData?.id);

            setUserRole({
              role: isOwner ? 'owner' : (currentOrgMember?.role || 'viewer'),
              is_superadmin: isSuperAdmin
            });

            // Set subscription status based on organisation (not user role)
            setSubscriptionStatus({
              tier: targetOrgData?.subscription_tier || 'demo',
              status: targetOrgData?.subscription_status || 'active'
            });
          } else {
            // No organisations found - check if user has company_name in metadata
            // and auto-create their first organisation
            const companyName = authUser.user_metadata?.company_name;

            if (companyName && userData?.id) {
              try {
                const { v4: uuidv4 } = await import('uuid');
                const newOrgId = uuidv4();

                // Create the organisation using the internal users.id for owner_id
                // The RLS policy allows this via the can_insert_organisation helper
                const { data: newOrg, error: createError } = await supabase
                  .from('organisations')
                  .insert({
                    id: newOrgId,
                    owner_id: userData.id, // Use internal users.id
                    name: companyName,
                    subscription_status: 'active',
                    subscription_tier: 'demo',
                    is_primary: true
                  })
                  .select()
                  .single();

                if (createError) {
                  console.error('Error creating initial organisation:', createError);
                } else if (newOrg) {
                  setOrganisations([newOrg]);
                  setCurrentOrg(newOrg);
                  localStorage.setItem('currentOrgId', newOrg.id);

                  // Set subscription status for new org
                  setSubscriptionStatus({
                    tier: 'demo',
                    status: 'active'
                  });

                  // Show welcome message
                  toast.success(`Welcome! Your organisation "${companyName}" has been created.`);
                }
              } catch (orgCreateError) {
                console.error('Error in auto-org creation:', orgCreateError);
              }
            } else {
              // No company_name in metadata - set default status
              setSubscriptionStatus({
                tier: 'demo',
                status: 'active'
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Fetch platform logo from system_settings
  useEffect(() => {
    const fetchPlatformLogo = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'platform_logo')
          .maybeSingle();

        if (data?.value) {
          const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
          if (parsed?.logo_url) {
            setPlatformLogo(parsed.logo_url);
          }
        }
      } catch (error) {
        // Silently fall back to default logo
      }
    };
    fetchPlatformLogo();
  }, []);

  // Update subscription status when current org changes
  useEffect(() => {
    if (currentOrg) {
      setSubscriptionStatus({
        tier: currentOrg.subscription_tier || 'demo',
        status: currentOrg.subscription_status || 'active'
      });
    }
  }, [currentOrg?.id, currentOrg?.subscription_tier]);

  // Fetch available financial years with data when org changes
  useEffect(() => {
    const fetchAvailableFYs = async () => {
      if (!currentOrg?.id) {
        setAvailableFYs([]);
        return;
      }

      try {
        // Get distinct financial years from emissions data
        const { data: emissions } = await supabase
          .from('emissions_data')
          .select('financial_year, entry_date')
          .eq('organisation_id', currentOrg.id);

        if (emissions && emissions.length > 0) {
          // Extract unique financial years
          const fys = new Set();
          emissions.forEach(e => {
            if (e.financial_year) {
              fys.add(e.financial_year);
            } else if (e.entry_date) {
              // Calculate FY from entry_date
              const date = new Date(e.entry_date);
              const fy = date.getMonth() >= 6 ? date.getFullYear() + 1 : date.getFullYear();
              fys.add(fy);
            }
          });

          // Sort descending
          const sortedFYs = Array.from(fys).sort((a, b) => b - a);
          setAvailableFYs(sortedFYs);

          // If selected year has no data, default to most recent or 'all'
          if (typeof selectedYear === 'number' && !sortedFYs.includes(selectedYear)) {
            const newYear = sortedFYs[0] || 'all';
            setSelectedYear(newYear);
            localStorage.setItem('selectedFY', String(newYear));
          }
        } else {
          // No data - show current FY
          const now = new Date();
          const currentFY = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
          setAvailableFYs([currentFY]);
        }
      } catch (error) {
        console.error('Error fetching available FYs:', error);
      }
    };

    fetchAvailableFYs();
  }, [currentOrg?.id]);

  // Fetch unread message count and set up realtime subscription
  useEffect(() => {
    if (!currentOrg?.id) return;

    const fetchUnreadCount = async () => {
      try {
        const { data, error } = await supabase
          .from('support_messages')
          .select('id, support_threads!inner(organisation_id)')
          .eq('support_threads.organisation_id', currentOrg.id)
          .eq('sender_type', 'admin')
          .eq('is_read', false);

        if (!error && data) {
          setUnreadMessageCount(data.length);
        }
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();

    // Set up realtime subscription for new admin messages
    const channel = supabase
      .channel('unread_messages_header')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_messages'
        },
        () => {
          // Refetch count on any message change
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrg?.id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.org-switcher') && !e.target.closest('[data-testid="context-block"]')) {
        setShowOrgSwitcher(false);
      }
      if (!e.target.closest('.notifications-dropdown') && !e.target.closest('[data-testid="notifications-btn"]')) {
        setShowNotifications(false);
      }
      if (!e.target.closest('.help-dropdown') && !e.target.closest('[data-testid="help-menu-btn"]')) {
        setShowHelpMenu(false);
      }
      if (!e.target.closest('.settings-dropdown') && !e.target.closest('[data-testid="settings-menu-btn"]')) {
        setShowSettingsMenu(false);
      }
      if (!e.target.closest('.lab-dropdown') && !e.target.closest('[data-testid="tab-aasb-s2-lab"]')) {
        setShowLabDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Switch organisation
  const switchOrganisation = (org) => {
    setCurrentOrg(org);
    localStorage.setItem('currentOrgId', org.id);
    setShowOrgSwitcher(false);
    toast.success(`Switched to ${org.name}`);

    // Navigate back to Dashboard and do a full page refresh 
    // to ensure userRole and permissions are fully re-evaluated.
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 600);
  };

  // Handle Add Organisation - shows modal
  const handleAddOrganisation = () => {
    setShowOrgSwitcher(false);
    setShowAddOrgModal(true);
  };

  // Create new organisation
  const createOrganisation = async () => {
    if (!newOrgData.name.trim()) {
      toast.error('Please enter an organisation name');
      return;
    }

    // Determine discount eligibility based on existing orgs
    const isEligibleForDiscount = organisations.some(
      org => org.user_role === 'owner' && (org.subscription_tier === 'pro' || org.subscription_tier === 'standard')
    );

    setCreatingOrg(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user) {
        throw new Error('Not authenticated');
      }
      const authUser = authData.user;

      // Get internal user ID
      const { data: userById } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .maybeSingle();

      // Use internal user ID if available, otherwise auth.uid
      const ownerId = userById?.id || authUser.id;
      // Try both approaches - first with internal ID, then with auth.uid if that fails
      let insertSuccess = false;
      let newOrg = null;

      // Attempt 1: Use internal user ID
      // Generate a UUID for the new organisation
      const newOrgId = uuidv4();
      // Attempt 1: Use internal user ID
      try {
        const result1 = await supabase
          .from('organisations')
          .insert({
            id: newOrgId,
            owner_id: ownerId,
            name: newOrgData.name.trim(),
            abn: newOrgData.abn || null,
            state: newOrgData.state || null,
            sector: newOrgData.sector || null,
            subscription_status: 'active',
            subscription_tier: 'demo',
            // Add-in logic: if eligible for discount, mark as add-on (is_primary: false)
            is_primary: !isEligibleForDiscount
          })
          .select()
          .single();
        if (result1.data && result1.data.id) {
          newOrg = result1.data;
          insertSuccess = true;
        }
      } catch (e1) {
      }

      // Attempt 2: If first failed and IDs are different, try with auth.uid directly
      if (!insertSuccess && ownerId !== authUser.id) {
        const newOrgId2 = uuidv4(); // New ID for second attempt
        try {
          const result2 = await supabase
            .from('organisations')
            .insert({
              id: newOrgId2,
              owner_id: authUser.id,
              name: newOrgData.name.trim(),
              abn: newOrgData.abn || null,
              state: newOrgData.state || null,
              sector: newOrgData.sector || null,
              subscription_status: 'active',
              subscription_tier: 'demo',
              // Add-in logic: if eligible for discount, mark as add-on (is_primary: false)
              is_primary: !isEligibleForDiscount
            })
            .select()
            .single();
          if (result2.data && result2.data.id) {
            newOrg = result2.data;
            insertSuccess = true;
          }
        } catch (e2) {
        }
      }

      // Check if insert succeeded by querying
      if (!insertSuccess) {
        // Small delay then check if org was created anyway
        await new Promise(resolve => setTimeout(resolve, 500));

        const { data: fetchedOrg } = await supabase
          .from('organisations')
          .select('*')
          .eq('name', newOrgData.name.trim())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fetchedOrg && fetchedOrg.id) {
          newOrg = fetchedOrg;
          insertSuccess = true;
        }
      }

      if (insertSuccess && newOrg) {
        // Success! 
        // Add audit log (fire and forget)
        supabase.from('audit_logs').insert({
          organisation_id: newOrg.id,
          resource_type: 'organisation',
          resource_id: newOrg.id,
          action: 'create',
          description: `Created organisation: ${newOrgData.name.trim()}`
        }).then(() => { }).catch(() => { });

        toast.success('Organisation created successfully!');
        setShowAddOrgModal(false);
        setNewOrgData({ name: '', abn: '', state: '', sector: '' });

        // Add to local organisations list and switch to it
        setOrganisations(prev => [...prev, newOrg]);
        setCurrentOrg(newOrg);
        localStorage.setItem('currentOrgId', newOrg.id);
        return;
      }

      // Failed to create
      toast.error('Unable to create organisation. Please contact support for assistance.');
    } catch (error) {
      console.error('Error creating organisation:', error);
      let errorMessage = 'Failed to create organisation';
      if (error && typeof error === 'object' && typeof error.message === 'string') {
        if (!error.message.includes('body stream')) {
          errorMessage = error.message;
        }
      }
      toast.error(errorMessage);
    } finally {
      setCreatingOrg(false);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Get notification count
  const notificationCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003B5C] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isPro = effectiveTier === 'pro';
  const isStandard = effectiveTier === 'standard';

  // Get display label for selected period
  const getPeriodLabel = () => {
    if (selectedYear === 'all') return 'All Time';
    if (selectedYear === 'custom') {
      if (customDateRange.startDate && customDateRange.endDate) {
        const start = new Date(customDateRange.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        const end = new Date(customDateRange.endDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        return `${start} - ${end}`;
      }
      return 'Custom Period';
    }
    return `FY ${selectedYear - 1}/${selectedYear}`;
  };

  // Tab definitions
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', route: '/dashboard', feature: FEATURES.DASHBOARD_VIEW },
    { id: 'emissions', label: 'Emissions', route: '/entries', feature: FEATURES.EMISSIONS_VIEW },
    { id: 'offsets', label: 'Offsets', route: '/offsets', feature: FEATURES.OFFSETS_VIEW },
    { id: 'reports', label: 'Reports', route: '/reports', feature: FEATURES.REPORTS_TABLE },
    { id: 'grants', label: 'Grants', route: '/grants', feature: FEATURES.GRANTS_SEARCH },
  ];

  const labItems = [
    { id: 'compliance', label: 'Compliance Check', route: '/compliance' },
    { id: 'governance', label: 'Governance', route: '/governance' },
    { id: 'risks', label: 'Climate Risks', route: '/risks' },
    { id: 'materiality', label: 'Materiality', route: '/materiality' },
    { id: 'social-metrics', label: 'Social Metrics', route: '/social-metrics' },
    { id: 'stress-test', label: 'Scenario Stress Test', route: '/stress-test' },
  ];

  // Determine active tab
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    if (path === '/entries') return 'emissions';
    if (path === '/offsets') return 'offsets';
    if (path === '/reports' || path === '/report-builder') return 'reports';
    if (path === '/grants') return 'grants';
    if (['/compliance', '/governance', '/risks', '/materiality', '/social-metrics', '/stress-test'].includes(path)) return 'lab';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab) => {
    const result = getAccessResult(tab.feature);
    if (!result.allowed) {
      setUpgradeFeature(tab.label);
      setUpgradeTarget(result.upgradeTarget);
      setShowUpgradeModal(true);
      return;
    }
    navigate(tab.route);
  };

  const handleLabClick = () => {
    const result = getAccessResult(FEATURES.AASB_S2_LAB);
    if (!result.allowed) {
      setUpgradeFeature('AASB S2 Lab');
      setUpgradeTarget(result.upgradeTarget);
      setShowUpgradeModal(true);
      return;
    }
    setShowLabDropdown(!showLabDropdown);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Single Sticky Header with Integrated Tabs */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Left: Logo */}
            <div className="flex items-center flex-shrink-0">
              <img
                src={platformLogo}
                alt="Lucent Sky ESG"
                className="h-7 w-auto object-contain cursor-pointer"
                onClick={() => navigate('/dashboard')}
                onError={(e) => { e.target.src = '/placeholder-logo.svg'; }}
              />
            </div>

            {/* Centre: Tab Navigation (Desktop) */}
            <TabNavigation
              activeTab={activeTab}
              subscriptionTier={effectiveTier}
              canPerformBilling={canAccess(FEATURES.SETTINGS_BILLING)}
              getAccessResult={getAccessResult}
              onUpgradeClick={() => navigate('/billing')}
              className="hidden lg:flex"
            />

            {/* Right: Org Selector + Icons */}
            <div className="flex items-center space-x-2">
              {/* Org & FY Selector */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowOrgSwitcher(!showOrgSwitcher)}
                  className="flex items-center space-x-2 text-left hover:bg-slate-50 px-3 py-1.5 rounded-lg transition-colors border border-slate-200"
                  data-testid="context-block"
                >
                  <div>
                    <div className="font-semibold text-[#003B5C] text-sm truncate max-w-[150px]">
                      {currentOrg?.name || 'Select Organisation'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {getPeriodLabel()}
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>

                {showOrgSwitcher && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 org-switcher">
                    {/* Organisations */}
                    <div className="p-2 border-b border-slate-100">
                      <div className="text-xs text-slate-400 uppercase tracking-wide px-2 py-1">Organisations</div>
                      {organisations.length > 0 ? organisations.map(org => (
                        <button
                          key={org.id}
                          onClick={() => switchOrganisation(org)}
                          className={`w-full px-3 py-2.5 text-left hover:bg-slate-50 rounded-lg flex items-center justify-between ${org.id === currentOrg?.id ? 'bg-slate-50' : ''}`}
                          data-testid={`org-item-${org.id}`}
                        >
                          <div>
                            <div className="font-medium text-sm text-[#003B5C]">{org.name}</div>
                            <div className="text-xs mt-0.5">
                              {org.subscription_tier === 'pro' || org.subscription_status === 'pro' ? (
                                <span className="text-[#74A57F] flex items-center">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Pro
                                </span>
                              ) : (
                                <span className="text-slate-500 capitalize">{org.subscription_tier || 'Demo'}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {org.user_role && (
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded capitalize">
                                {org.user_role.replace('_', ' ')}
                              </span>
                            )}
                            {org.is_primary ? (
                              <span className="text-xs bg-[#003B5C] text-white px-2 py-0.5 rounded">Primary</span>
                            ) : (
                              <span className="text-xs bg-[#74A57F] text-white px-2 py-0.5 rounded">Add-on</span>
                            )}
                            {org.id === currentOrg?.id && <Check className="w-4 h-4 text-[#74A57F]" />}
                          </div>
                        </button>
                      )) : (
                        <div className="px-3 py-2 text-sm text-slate-500">No organisations</div>
                      )}

                      {/* Add Organisation Button */}
                      {(() => {
                        const isEligibleForDiscount = organisations.some(
                          org => org.user_role === 'owner' && (org.subscription_tier === 'pro' || org.subscription_tier === 'standard')
                        );
                        return (
                          <button
                            onClick={handleAddOrganisation}
                            className="w-full px-3 py-2.5 text-left text-sm text-[#74A57F] hover:bg-[#74A57F]/10 rounded-lg flex items-center justify-between mt-1"
                            data-testid="add-org-button"
                          >
                            <div className="flex items-center">
                              <Plus className="w-4 h-4 mr-2" />
                              + Add New Organisation
                            </div>
                            {isEligibleForDiscount && (
                              <span className="text-[10px] bg-[#74A57F]/10 text-[#5F8575] px-1.5 py-0.5 rounded font-medium">
                                20% Discount Eligible
                              </span>
                            )}
                          </button>
                        );
                      })()}
                    </div>

                    {/* Reporting Period */}
                    <div className="p-2 max-h-64 overflow-y-auto">
                      <div className="text-xs text-slate-400 uppercase tracking-wide px-2 py-1">Reporting Period</div>

                      <button
                        onClick={() => { setSelectedYear('all'); localStorage.setItem('selectedFY', 'all'); setShowOrgSwitcher(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 rounded-lg flex items-center justify-between ${selectedYear === 'all' ? 'bg-slate-50 font-medium' : ''}`}
                      >
                        <span>All Time</span>
                        {selectedYear === 'all' && <Check className="w-4 h-4 text-[#74A57F]" />}
                      </button>

                      <button
                        onClick={() => { setSelectedYear('custom'); localStorage.setItem('selectedFY', 'custom'); setShowCustomDatePicker(true); setShowOrgSwitcher(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 rounded-lg flex items-center justify-between ${selectedYear === 'custom' ? 'bg-slate-50 font-medium' : ''}`}
                      >
                        <span>Custom Period...</span>
                        {selectedYear === 'custom' && <Check className="w-4 h-4 text-[#74A57F]" />}
                      </button>

                      <div className="border-t border-slate-100 my-2"></div>

                      {/* Show available FYs or current FY if no data */}
                      {(availableFYs.length > 0 ? availableFYs : [(() => {
                        const now = new Date();
                        return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
                      })()]).map(year => (
                        <button
                          key={year}
                          onClick={() => { setSelectedYear(year); localStorage.setItem('selectedFY', String(year)); setShowOrgSwitcher(false); }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 rounded-lg flex items-center justify-between ${year === selectedYear ? 'bg-slate-50 font-medium' : ''}`}
                        >
                          FY {year - 1}/{year}
                          {year === selectedYear && <Check className="w-4 h-4 text-[#74A57F]" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications */}
              {/* Messages */}
              <div className="relative">
                <Button
                  onClick={() => setShowMessageDrawer(true)}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-slate-100"
                  data-testid="messages-btn"
                >
                  <Mail className="w-5 h-5 text-slate-600" />
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </Button>
              </div>

              {/* Notifications */}
              <div className="relative">
                <Button
                  onClick={() => setShowNotifications(!showNotifications)}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-slate-100"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </Button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-50 notifications-dropdown">
                    <div className="px-4 py-3 border-b border-slate-200">
                      <h4 className="font-semibold text-[#003B5C]">Notifications</h4>
                    </div>
                    <div className="p-4 text-center text-slate-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Help */}
              <div className="relative">
                <Button
                  onClick={() => setShowHelpMenu(!showHelpMenu)}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-slate-100"
                  data-testid="help-menu-btn"
                >
                  <HelpCircle className="w-5 h-5 text-slate-600" />
                </Button>

                {showHelpMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 help-dropdown">
                    {canAccess(FEATURES.HELP_CENTRE) && (
                      <button
                        onClick={() => { navigate('/help'); setShowHelpMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <HelpCircle className="w-4 h-4 text-blue-500" />
                        <div>
                          <div className="font-medium text-slate-800">Help Centre</div>
                          <div className="text-xs text-slate-500">Guides & documentation</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.HELP_IDEAS_PORTAL) && (
                      <button
                        onClick={() => { navigate('/ideas'); setShowHelpMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        <div>
                          <div className="font-medium text-slate-800">Ideas Portal</div>
                          <div className="text-xs text-slate-500">Suggest features & improvements</div>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="relative">
                <Button
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-slate-100"
                  data-testid="settings-menu-btn"
                >
                  <Settings className="w-5 h-5 text-slate-600" />
                </Button>

                {showSettingsMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 settings-dropdown">
                    {canAccess(FEATURES.SETTINGS_ADMIN_PANEL) && (
                      <button
                        onClick={() => { navigate('/admin'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Settings className="w-4 h-4 text-red-500" />
                        <div>
                          <div className="font-medium text-slate-800">Admin Panel</div>
                          <div className="text-xs text-slate-500">User & factor management</div>
                        </div>
                      </button>
                    )}
                    <button
                      onClick={() => { navigate('/my-account'); setShowSettingsMenu(false); }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      data-testid="my-account-menu-item"
                    >
                      <Users className="w-4 h-4 text-[#74A57F]" />
                      <div>
                        <div className="font-medium text-slate-800">My Account</div>
                        <div className="text-xs text-slate-500">Profile & password</div>
                      </div>
                    </button>
                    {canAccess(FEATURES.SETTINGS_ORG_PROFILE) && (
                      <button
                        onClick={() => { navigate('/profile'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Building2 className="w-4 h-4 text-[#003B5C]" />
                        <div>
                          <div className="font-medium text-slate-800">Organisation Profile</div>
                          <div className="text-xs text-slate-500">Business details & settings</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.SETTINGS_TEAM_MGMT) && (
                      <button
                        onClick={() => { navigate('/team'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Users className="w-4 h-4 text-blue-500" />
                        <div>
                          <div className="font-medium text-slate-800">Team Management</div>
                          <div className="text-xs text-slate-500">Invite & manage team members</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.SETTINGS_BILLING) && (
                      <button
                        onClick={() => { navigate('/billing'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Crown className="w-4 h-4 text-[#74A57F]" />
                        <div>
                          <div className="font-medium text-slate-800">Billing & Subscription</div>
                          <div className="text-xs text-slate-500">Manage your plan</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.SETTINGS_ACTIVITY_LOG) && (
                      <button
                        onClick={() => { navigate('/activity-log'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Activity className="w-4 h-4 text-purple-500" />
                        <div>
                          <div className="font-medium text-slate-800">Activity Log</div>
                          <div className="text-xs text-slate-500">View audit trail</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.SETTINGS_DATA_EXPORT) && (
                      <button
                        onClick={() => { navigate('/data-export'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Download className="w-4 h-4 text-purple-500" />
                        <div>
                          <div className="font-medium text-slate-800">Data Export</div>
                          <div className="text-xs text-slate-500">Download organisation data</div>
                        </div>
                      </button>
                    )}
                    {canAccess(FEATURES.SETTINGS_PUBLIC_PROFILE) && (
                      <button
                        onClick={() => { window.open(`/profile/${currentOrg?.public_profile_slug}`, '_blank'); setShowSettingsMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                      >
                        <Globe className="w-4 h-4 text-blue-500" />
                        <div>
                          <div className="font-medium text-slate-800">Public Profile</div>
                          <div className="text-xs text-slate-500">Share ESG credentials</div>
                        </div>
                      </button>
                    )}
                    <button
                      onClick={() => { toast.info('Integrations coming soon!'); setShowSettingsMenu(false); }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center space-x-3 opacity-60"
                    >
                      <Zap className="w-4 h-4 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-500">Integrations</div>
                        <div className="text-xs text-slate-400">Xero, MYOB, Utilities</div>
                      </div>
                      <span className="ml-auto bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-medium">SOON</span>
                    </button>

                    <div className="border-t border-slate-100 my-1"></div>

                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 flex items-center space-x-3 text-red-600"
                    >
                      <LogOut className="w-4 h-4" />
                      <div className="font-medium">Sign Out</div>
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <Button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                variant="ghost"
                size="icon"
                className="lg:hidden hover:bg-slate-100"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="lg:hidden bg-white border-b border-slate-200 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {/* Mobile Tabs */}
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    const result = getAccessResult(tab.feature);
                    if (!result.allowed) {
                      setUpgradeFeature(tab.label);
                      setUpgradeTarget(result.upgradeTarget);
                      setShowUpgradeModal(true);
                    } else {
                      navigate(tab.route);
                    }
                    setShowMobileMenu(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center justify-between 
                    ${activeTab === tab.id ? 'bg-[#003B5C] text-white' : 'text-slate-700 hover:bg-slate-50'} 
                    ${!canAccess(tab.feature) ? 'opacity-50 grayscale' : ''}`}
                >
                  {tab.label}
                  {!canAccess(tab.feature) && <Lock className="w-3 h-3 text-slate-400" />}
                </button>
              ))}

              {/* Mobile AASB S2 Lab */}
              <button
                onClick={() => {
                  const result = getAccessResult(FEATURES.AASB_S2_LAB);
                  if (!result.allowed) {
                    setUpgradeFeature('AASB S2 Lab');
                    setUpgradeTarget(result.upgradeTarget);
                    setShowUpgradeModal(true);
                  } else {
                    navigate('/compliance');
                  }
                  setShowMobileMenu(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center justify-between 
                  ${activeTab === 'lab' ? 'bg-[#003B5C] text-white' : 'text-slate-700 hover:bg-slate-50'} 
                  ${!canAccess(FEATURES.AASB_S2_LAB) ? 'opacity-50 grayscale' : ''}`}
              >
                <span>AASB S2 Lab <span className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded font-semibold ml-1">BETA</span></span>
                {!canAccess(FEATURES.AASB_S2_LAB) && <Lock className="w-3 h-3 text-slate-400" />}
              </button>

              <div className="border-t border-slate-200 my-2 pt-2">
                <button
                  onClick={() => { navigate('/help'); setShowMobileMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 rounded-lg flex items-center"
                >
                  <HelpCircle className="w-4 h-4 mr-3 text-blue-500" />
                  Help Centre
                </button>
                <button
                  onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Upgrade Modal (For mobile menu use) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowUpgradeModal(false); setUpgradeTarget(null); }}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-[#003B5C] mb-2">{upgradeFeature}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {upgradeFeature === 'Offsets'
                  ? 'Track and allocate carbon offsets to demonstrate your commitment to net zero.'
                  : upgradeFeature === 'AASB S2 Lab'
                    ? 'Access advanced AASB S2 readiness tools to prepare for mandatory climate reporting in FY 2027/28.'
                    : upgradeTarget === 'standard_or_pro'
                      ? `Access ${upgradeFeature} and more by upgrading your plan.`
                      : `Access ${upgradeFeature} and more with our Pro plan.`
                }
              </p>
              <div className="bg-purple-50 rounded-lg p-3 mb-4">
                <p className="text-purple-700 text-sm font-medium">
                  {upgradeTarget === 'standard_or_pro' ? 'Available on Standard & Pro Plans' : 'Available on Pro Plan'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowUpgradeModal(false); setUpgradeTarget(null); }}>
                  Maybe Later
                </Button>
                <Button
                  className={`flex-1 ${!canAccess(FEATURES.SETTINGS_BILLING) ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600'}`}
                  onClick={() => {
                    if (!canAccess(FEATURES.SETTINGS_BILLING)) {
                      toast.error("You don't have permission to manage billing. Please contact your administrator.");
                      return;
                    }
                    setShowUpgradeModal(false);
                    setUpgradeTarget(null);
                    navigate('/billing');
                  }}
                >
                  {upgradeTarget === 'standard_or_pro' ? 'View Plans' : 'Upgrade to Pro'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      {(currentOrg?.subscription_override && (!currentOrg?.subscription_override_expiry || new Date(currentOrg.subscription_override_expiry) > new Date())) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center space-x-3">
            <Shield className="w-5 h-5 text-amber-500" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">Manual Subscription Override Active</span> — This organisation has been granted temporary access to the <span className="capitalize font-semibold">{effectiveTier}</span> tier until {new Date(currentOrg.subscription_override_expiry).toLocaleDateString()}.
            </p>
          </div>
        </div>
      )}
      <Outlet context={{ currentOrg, setCurrentOrg, organisations, subscriptionStatus, userRole, selectedYear, customDateRange, user, userData, canPerform, isAdmin: isSuperAdminOrAdmin, effectiveTier, effectiveRole, platformLogo, setPlatformLogo }} />

      {/* Custom Date Range Picker Modal */}
      {showCustomDatePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="custom-date-picker-modal">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Select Custom Date Range
              </h2>
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setTempDateRange({ start: null, end: null });
                  // If no valid custom range was set, revert to all time
                  if (!customDateRange.startDate || !customDateRange.endDate) {
                    setSelectedYear('all');
                    localStorage.setItem('selectedFY', 'all');
                  }
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Start Date */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Start Date</Label>
                <div className="border rounded-lg p-2 bg-slate-50">
                  <Calendar
                    mode="single"
                    selected={tempDateRange.start || (customDateRange.startDate ? new Date(customDateRange.startDate) : undefined)}
                    onSelect={(date) => setTempDateRange(prev => ({ ...prev, start: date }))}
                    disabled={(date) => tempDateRange.end ? date > tempDateRange.end : false}
                    initialFocus
                    className="mx-auto"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {tempDateRange.start ? tempDateRange.start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : (customDateRange.startDate ? new Date(customDateRange.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not selected')}
                </p>
              </div>

              {/* End Date */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">End Date</Label>
                <div className="border rounded-lg p-2 bg-slate-50">
                  <Calendar
                    mode="single"
                    selected={tempDateRange.end || (customDateRange.endDate ? new Date(customDateRange.endDate) : undefined)}
                    onSelect={(date) => setTempDateRange(prev => ({ ...prev, end: date }))}
                    disabled={(date) => tempDateRange.start ? date < tempDateRange.start : false}
                    className="mx-auto"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {tempDateRange.end ? tempDateRange.end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : (customDateRange.endDate ? new Date(customDateRange.endDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not selected')}
                </p>
              </div>
            </div>

            {/* Quick Select Options */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Quick Select</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth(), 1);
                    setTempDateRange({ start, end: today });
                  }}
                  className="text-xs"
                >
                  This Month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
                    setTempDateRange({ start, end: today });
                  }}
                  className="text-xs"
                >
                  Last 3 Months
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
                    setTempDateRange({ start, end: today });
                  }}
                  className="text-xs"
                >
                  Last 6 Months
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
                    setTempDateRange({ start, end: today });
                  }}
                  className="text-xs"
                >
                  Last 12 Months
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), 0, 1);
                    setTempDateRange({ start, end: today });
                  }}
                  className="text-xs"
                >
                  This Calendar Year
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const lastYear = new Date().getFullYear() - 1;
                    const start = new Date(lastYear, 0, 1);
                    const end = new Date(lastYear, 11, 31);
                    setTempDateRange({ start, end });
                  }}
                  className="text-xs"
                >
                  Last Calendar Year
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setTempDateRange({ start: null, end: null });
                  // If no valid custom range, revert
                  if (!customDateRange.startDate || !customDateRange.endDate) {
                    setSelectedYear('all');
                    localStorage.setItem('selectedFY', 'all');
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const startDate = tempDateRange.start || (customDateRange.startDate ? new Date(customDateRange.startDate) : null);
                  const endDate = tempDateRange.end || (customDateRange.endDate ? new Date(customDateRange.endDate) : null);

                  if (!startDate || !endDate) {
                    toast.error('Please select both start and end dates');
                    return;
                  }

                  if (startDate > endDate) {
                    toast.error('Start date must be before end date');
                    return;
                  }

                  // Store the date range
                  setCustomDateRange({
                    startDate: startDate.toISOString().split('T')[0],
                    endDate: endDate.toISOString().split('T')[0]
                  });
                  setSelectedYear('custom');
                  localStorage.setItem('selectedFY', 'custom');
                  setShowCustomDatePicker(false);
                  setShowOrgSwitcher(false);
                  setTempDateRange({ start: null, end: null });
                  toast.success(`Viewing data from ${startDate.toLocaleDateString('en-AU')} to ${endDate.toLocaleDateString('en-AU')}`);
                }}
                className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                data-testid="apply-date-range-btn"
              >
                Apply Date Range
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Organisation Modal */}
      {showAddOrgModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddOrgModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#74A57F] to-[#003B5C] rounded-xl flex items-center justify-center mr-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Add New Organisation
                </h2>
                <p className="text-sm text-slate-500">Create a new organisation to track emissions</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-[#003B5C]">Organisation Name *</Label>
                <Input
                  value={newOrgData.name}
                  onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                  placeholder="Your organisation name"
                  className="mt-1"
                  data-testid="new-org-name"
                />
              </div>
              <div>
                <Label className="text-[#003B5C]">ABN (Optional)</Label>
                <Input
                  value={newOrgData.abn}
                  onChange={(e) => setNewOrgData({ ...newOrgData, abn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                  placeholder="12 345 678 901"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[#003B5C]">State/Territory</Label>
                <Select
                  value={newOrgData.state}
                  onValueChange={(val) => setNewOrgData({ ...newOrgData, state: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select your state" />
                  </SelectTrigger>
                  <SelectContent>
                    {australianStates.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#003B5C]">Sector</Label>
                <Select
                  value={newOrgData.sector}
                  onValueChange={(val) => setNewOrgData({ ...newOrgData, sector: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectorOptions.map((sector) => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mt-4">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> New organisations start on the Demo plan. You can upgrade to Pro for additional features.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={createOrganisation}
                  disabled={creatingOrg || !newOrgData.name.trim()}
                  className="flex-1 bg-[#003B5C] text-white hover:bg-[#002A42]"
                  data-testid="create-org-btn"
                >
                  {creatingOrg ? 'Creating...' : 'Create Organisation'}
                </Button>
                <Button
                  onClick={() => {
                    setShowAddOrgModal(false);
                    setNewOrgData({ name: '', abn: '', state: '', sector: '' });
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Co-Pilot */}
      <AICoPilot
        organisationId={currentOrg?.id}
        organisationName={currentOrg?.name}
        subscriptionTier={subscriptionStatus?.tier || 'demo'}
        currentPage="general"
      />

      {/* Message Drawer */}
      <MessageDrawer
        isOpen={showMessageDrawer}
        onClose={() => setShowMessageDrawer(false)}
        organisationId={currentOrg?.id}
        organisationName={currentOrg?.name}
      />
    </div>
  );
}
